import { ActionIcon, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCheck, IconCopy, IconKey, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import type { CredentialListItem } from '@xver/apis/v1/kufer/registry-credentials/model';
import { Button, ConfirmActionModal } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { DataTable, DataTableColumn } from '@xver/components/Organisms';
import { copyToClipboard } from '@xver/utils/clipboard';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCredentialMutations } from '../hooks/useCredentialMutations';
import { useRegistryCredentialsQuery } from '../hooks/useRegistryCredentialsQuery';
import CredentialFormModal from './CredentialFormModal';

function shortenId(id: string): string {
    if (id.length <= 12) return id;
    return `${id.slice(0, 6)}…${id.slice(-4)}`;
}

export default function CredentialsTabPanel() {
    const { t } = useTranslation('containerEngine');
    const { data, isPending } = useRegistryCredentialsQuery();
    const { deleteMutation } = useCredentialMutations();

    const credentials = data?.credentials ?? [];

    const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
    const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false);
    const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);
    const [editingId, setEditingId] = useState<string | undefined>();
    const [pendingDelete, setPendingDelete] = useState<CredentialListItem | null>(null);

    const handleEdit = (c: CredentialListItem) => {
        setEditingId(c.id);
        openEdit();
    };

    const handleDelete = (c: CredentialListItem) => {
        setPendingDelete(c);
        openDelete();
    };

    const confirmDelete = () => {
        if (!pendingDelete) return;
        deleteMutation.mutate(pendingDelete.id, {
            onSuccess: () => {
                closeDelete();
                setPendingDelete(null);
            },
        });
    };

    const handleCopyId = async (c: CredentialListItem) => {
        try {
            await copyToClipboard(c.id);
            notifications.show({
                title: t('credentials.toast.copyTitle', 'Credential ID copied'),
                message: c.id,
                icon: <IconCheck size={18} color="#22c55e" />,
            });
        } catch {
            // copyToClipboard fallback handles its own failure modes; no toast on error.
        }
    };

    const columns: DataTableColumn<CredentialListItem>[] = [
        {
            key: 'name',
            label: t('credentials.columns.name', 'Name'),
            render: (c) => <span className="font-medium text-fg">{c.name}</span>,
        },
        {
            key: 'endpoint',
            label: t('credentials.columns.endpoint', 'Endpoint'),
            render: (c) => <span className="text-fg/85">{c.endpoint}</span>,
        },
        {
            key: 'id',
            label: t('credentials.columns.id', 'ID'),
            render: (c) => (
                <div className="inline-flex items-center gap-2">
                    <code className="text-xs text-fg/68 font-mono">{shortenId(c.id)}</code>
                    <Tooltip label={t('credentials.columns.copyId', 'Copy ID')} withArrow>
                        <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="sm"
                            onClick={() => void handleCopyId(c)}
                        >
                            <IconCopy size={14} />
                        </ActionIcon>
                    </Tooltip>
                </div>
            ),
        },
    ];

    return (
        <>
            <DialogBox
                label={t('credentials.title', 'Registry credentials')}
                description={t(
                    'credentials.description',
                    'Saved credentials for pulling images from private container registries.'
                )}
                rightDescription={
                    <Button
                        variant="primary"
                        size="sm"
                        leftIcon={<IconPlus size={14} />}
                        onClick={openCreate}
                    >
                        {t('credentials.createButton', 'New credential')}
                    </Button>
                }
            >
                <DataTable
                    caption={t('credentials.title', 'Registry credentials')}
                    columns={columns}
                    rows={credentials}
                    isLoading={isPending}
                    rowKey={(c) => c.id}
                    emptyMessage={
                        <div className="w-full flex flex-col items-center justify-center gap-2 py-6">
                            <IconKey size={28} stroke={1.25} className="text-fg/40" />
                            <span className="text-sm text-fg/68">
                                {t(
                                    'credentials.empty',
                                    'No credentials yet. Add one to pull from private registries.'
                                )}
                            </span>
                        </div>
                    }
                    actions={(c) => (
                        <div className="flex flex-row justify-end items-center gap-1">
                            <Tooltip label={t('credentials.editAction', 'Edit')} withArrow>
                                <ActionIcon
                                    variant="subtle"
                                    color="gray"
                                    onClick={() => handleEdit(c)}
                                    aria-label={t('credentials.editAction', 'Edit')}
                                >
                                    <IconPencil size={16} />
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip label={t('credentials.deleteAction', 'Delete')} withArrow>
                                <ActionIcon
                                    variant="subtle"
                                    color="red"
                                    onClick={() => handleDelete(c)}
                                    aria-label={t('credentials.deleteAction', 'Delete')}
                                >
                                    <IconTrash size={16} />
                                </ActionIcon>
                            </Tooltip>
                        </div>
                    )}
                />
            </DialogBox>

            <CredentialFormModal mode="create" opened={createOpened} onClose={closeCreate} />
            <CredentialFormModal
                mode="edit"
                opened={editOpened}
                credentialId={editingId}
                onClose={() => {
                    closeEdit();
                    setEditingId(undefined);
                }}
            />

            <ConfirmActionModal
                opened={deleteOpened}
                onClose={() => {
                    if (deleteMutation.isPending) return;
                    closeDelete();
                    setPendingDelete(null);
                }}
                title={t('credentials.deleteConfirmTitle', 'Delete credential?')}
                message={t('credentials.deleteConfirmBody', {
                    name: pendingDelete?.name ?? '',
                    defaultValue:
                        '"{{name}}" will be removed from your account. This cannot be undone.',
                })}
                confirmLabel={t('credentials.deleteConfirmLabel', 'Delete credential')}
                confirmVariant="danger"
                isLoading={deleteMutation.isPending}
                onConfirm={confirmDelete}
            />
        </>
    );
}
